import { extractQueryOutput, mapWidgetsToSchoolReportData } from '@/lib/mapSchoolReportWidgets';
import type { SchoolReportData } from '@/types/schoolReport';
import { getApiClient } from '@/api/clientRegistry';
import { ApiResponse } from '@/types/api';
import { SearchSchoolData } from '@/types/school';

function requireEnv(key: string): string {
  const raw = import.meta.env[key as keyof ImportMetaEnv];
  const value = typeof raw === 'string' ? raw.trim() : '';
  if (value) return value;
  if (import.meta.env.DEV && key === 'ANALYTICS_URL') return '/analytics';
  throw new Error(`[api] Missing required environment variable "${key}".`);
}

function getAnalyticsConfig() {
  return {
    baseUrl: requireEnv('ANALYTICS_URL').replace(/\/$/, ''),
    dashboardId: requireEnv('ANALYTICS_DASHBOARD_ID'),
    productId: requireEnv('ANALYTICS_PRODUCT_ID'),
    tenantId: requireEnv('ANALYTICS_TENANT_ID'),
    gradewiseWidgetId: requireEnv('ANALYTICS_WIDGET_GRADEWISE_ID'),
    sectionwiseWidgetId: requireEnv('ANALYTICS_WIDGET_SECTIONWISE_ID'),
    studentwiseWidgetId: requireEnv('ANALYTICS_WIDGET_STUDENTWISE_ID'),
  };
}

const oneAppBFFClient = getApiClient("oneappAdminBFF")

async function postWidget(
  widgetId: string,
  schoolId: string,
  pagination?: { page: number; per_page: number }
): Promise<Record<string, unknown>[]> {
  const { dashboardId, productId, tenantId } = getAnalyticsConfig();
  const payload = {
    widget_id: widgetId,
    dashboard_id: dashboardId,
    product_id: productId,
    tenant_id: tenantId,
    filters: {
      SCHOOL_ID: schoolId,
    },
    ...(pagination ?? {}),
  };

  const { data } = await oneAppBFFClient.post<unknown>('/app/v1/genius-school-report/widgets', payload);
  return extractQueryOutput(data, widgetId);
}

/** Dedupes concurrent fetches (e.g. React Strict Mode double-mount in dev). */
const inflightBySchoolId = new Map<string, Promise<SchoolReportData>>();

async function loadSchoolReport(schoolId: string): Promise<SchoolReportData> {
  const {
    gradewiseWidgetId,
    sectionwiseWidgetId,
    studentwiseWidgetId,
  } = getAnalyticsConfig();

  const [gradewiseRows, sectionwiseRows, studentwiseRows] = await Promise.all([
    postWidget(gradewiseWidgetId, schoolId),
    postWidget(sectionwiseWidgetId, schoolId),
    postWidget(studentwiseWidgetId, schoolId, { page: 1, per_page: 50 }),
  ]);

  return mapWidgetsToSchoolReportData(schoolId, {
    gradewiseRows,
    sectionwiseRows,
    studentwiseRows,
  });
}

/** Fetches gradewise + sectionwise + studentwise widgets and maps to School Report UI data. */
export async function fetchSchoolReportBySchoolId(
  schoolId: string
): Promise<SchoolReportData> {
  const trimmed = schoolId.trim();
  if (!trimmed) {
    throw new Error('SCHOOL_ID is required');
  }

  const existing = inflightBySchoolId.get(trimmed);
  if (existing) return existing;

  const request = loadSchoolReport(trimmed).finally(() => {
    inflightBySchoolId.delete(trimmed);
  });
  inflightBySchoolId.set(trimmed, request);
  return request;
}

export const getSchoolDetailsPublic = async (schoolId: string) => {
  const requestBody = {
    school_code: schoolId,
  };
  const response = await oneAppBFFClient.post<ApiResponse<SearchSchoolData>>(
    "app/v1/school/search-school",
    requestBody,
    {
      params: {
        page: 1,
        page_size: 100,
      },
    }
  );

  return response.data;
};
